import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { useRetractRequest } from './useRetractRequest'

interface UseRetractInlineFlowParams {
  /** The page heading shown before the retract flow starts. */
  defaultHeading: string
  requestId: string
  fallbackNuid?: string
}

export interface UseRetractInlineFlowResult {
  /** Current page heading — updates as the retract flow progresses. */
  heading: string
  /** True while the inline retract form is visible. */
  isRetractOpen: boolean
  /** True while the submit API call is in flight. */
  isSubmittingRetract: boolean
  /** True once the retraction has been successfully submitted. */
  retractSubmitted: boolean
  /** "My Approver asked" checkbox state, bound to RetractForm. */
  partialRetract: boolean
  setPartialRetract: Dispatch<SetStateAction<boolean>>
  /** Comments textarea value, bound to RetractForm. */
  comments: string
  setComments: Dispatch<SetStateAction<string>>
  /** API-level error message, if any. */
  retractError: string | null
  /** Dispatch the retract API call. */
  submitRetractRequest: () => Promise<boolean>
  /** Open the inline retract form. */
  openRetract: () => void
  /** Close the inline retract form and reset all form state. */
  handleCancel: () => void
}

/**
 * Encapsulates the shared retract-flow logic used by OverriddenView and
 * PartiallyApprovedTimeOffView: open/close state, heading computation, and
 * the useRetractRequest wiring. Pass `defaultHeading` to set the initial
 * page heading; the hook derives the retract and submitted headings automatically.
 */
export function useRetractInlineFlow({
  defaultHeading,
  requestId,
  fallbackNuid,
}: UseRetractInlineFlowParams): UseRetractInlineFlowResult {
  const [isRetractOpen, setIsRetractOpen] = useState(false)

  const {
    partialRetract,
    setPartialRetract,
    comments,
    setComments,
    retractError,
    isSubmittingRetract,
    retractSubmitted,
    submitRetractRequest,
    resetRetractForm,
  } = useRetractRequest({ requestId, fallbackNuid })

  let heading = defaultHeading
  if (retractSubmitted) {
    heading = 'Request for Time Off Retraction Submitted'
  } else if (isRetractOpen) {
    heading = 'Retract an Approved Time Off Request'
  }

  const openRetract = () => setIsRetractOpen(true)

  const handleCancel = () => {
    setIsRetractOpen(false)
    resetRetractForm()
  }

  return {
    heading,
    isRetractOpen,
    isSubmittingRetract,
    retractSubmitted,
    partialRetract,
    setPartialRetract,
    comments,
    setComments,
    retractError,
    submitRetractRequest,
    openRetract,
    handleCancel,
  }
}
